import { BdFeatureStatusService } from '@bindoc-projects/security-lib/data';
import { BdFeaturePermissionService } from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  BdFeature,
  BdFeatureNavigation,
} from '@bindoc-projects/feature-catalog-domain-lib';
import {
  combineLatest,
  distinctUntilChanged,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  timer,
} from 'rxjs';
import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MenuItem } from 'primeng/api';

export class BdMenuHandler {
  public menu$: ReplaySubject<MenuItem[]> = new ReplaySubject<MenuItem[]>(1);
  public homeItem$: ReplaySubject<MenuItem | null> =
    new ReplaySubject<MenuItem | null>(1);
  public kliaItem$: ReplaySubject<MenuItem | null> =
    new ReplaySubject<MenuItem | null>(1);

  constructor(
    private readonly _availableFeatures: BdFeature<string>[],
    private readonly _authentication$: Observable<boolean>,
    private readonly _msalInitialized: Observable<void>,
    private readonly _featureStatusService: BdFeatureStatusService,
    private readonly _featurePermissionService: BdFeaturePermissionService,
    private readonly _destroyRef: DestroyRef,
  ) {
    this._init();
  }

  private _init(): void {
    const status$ = timer(0, 60 * 1_000).pipe(
      switchMap(() => this._msalInitialized),
      switchMap(() => {
        return this._featureStatusService.getFeatureStatus(
          this._availableFeatures.map((it) => it.id),
        );
      }),
      distinctUntilChanged(
        (previous, current) =>
          JSON.stringify(previous) === JSON.stringify(current),
      ),
    );

    const authAndPermission$ = this._msalInitialized.pipe(
      switchMap(() => this._authentication$),
      distinctUntilChanged(),
      switchMap((authenticated) => {
        if (authenticated) {
          // BdFeaturePermissionService.getPermissions returns the union of
          // feature and admin permissions, so the menu's visibility/lock
          // checks see admin entries (e.g. ADMIN:FEATURE) too.
          //
          // Forced on purpose: a newly granted feature permission has to light
          // up its menu entry on the next page load, without waiting for a new
          // tab. `force` only refreshes the feature permissions — admin
          // permissions come from the session cache (see the service).
          return this._featurePermissionService.getPermissions(true);
        } else {
          return of(new Array<`${string}:${string}`>());
        }
      }),
    );

    combineLatest([status$, authAndPermission$])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((data) => {
        this._update(data[0], data[1]);
      });
  }

  private _update(
    featureStatus: { [featureId: string]: boolean },
    featurePermissions: `${string}:${string}`[],
  ): void {
    const homeFeature = this._availableFeatures.find((it) => it.id === 'HOME');
    const kliaFeature = this._availableFeatures.find((it) => it.id === 'KLIA');
    const remainingFeatures = this._availableFeatures.filter(
      (it) => it.id !== 'HOME' && it.id !== 'KLIA',
    );

    if (homeFeature) {
      const homeItem = this._getMenuItem(
        homeFeature,
        featureStatus,
        featurePermissions,
      );
      // The Home row highlights on an exact match only: its path is a prefix of
      // every child route below it ('/home/content', …), so a prefix match would
      // keep both the parent and the child marked active.
      homeItem.routerLinkActiveOptions ??= { exact: true };
      this.homeItem$.next(homeItem);
    } else {
      this.homeItem$.next(null);
    }

    if (kliaFeature) {
      this.kliaItem$.next(
        this._getMenuItem(kliaFeature, featureStatus, featurePermissions),
      );
    } else {
      this.kliaItem$.next(null);
    }

    // Plain reduce instead of lodash `groupBy`: this handler is on every app's
    // eager startup path and the import pulled the whole lodash CJS build in.
    // Key order = first occurrence, same as lodash.
    const _groupBy = remainingFeatures.reduce<
      Record<string, BdFeature<string>[]>
    >((groups, it) => {
      (groups[it.type] ??= []).push(it);
      return groups;
    }, {});

    const _menu = Object.keys(_groupBy)
      .map((type) => {
        let menuItems: MenuItem[];
        switch (type) {
          case 'tool':
            menuItems = [
              {
                label: 'Tools',
                expanded: true,
                items: _groupBy[type].map((it) =>
                  this._getMenuItem(it, featureStatus, featurePermissions),
                ),
              },
            ];
            break;
          case 'resource':
            menuItems = [
              {
                label: 'Ressourcen',
                expanded: false,
                items: _groupBy[type].map((it) =>
                  this._getMenuItem(it, featureStatus, featurePermissions),
                ),
              },
            ];
            break;
          case 'users':
            menuItems = [
              {
                label: 'Nutzer',
                expanded: false,
                items: _groupBy[type].map((it) =>
                  this._getMenuItem(it, featureStatus, featurePermissions),
                ),
              },
            ];
            break;
          case 'companies':
            menuItems = [
              {
                label: 'Kunden',
                expanded: true,
                items: _groupBy[type].map((it) =>
                  this._getMenuItem(it, featureStatus, featurePermissions),
                ),
              },
            ];
            break;
          case 'jobs':
            menuItems = [
              {
                label: 'Jobs',
                expanded: true,
                items: _groupBy[type].map((it) =>
                  this._getMenuItem(it, featureStatus, featurePermissions),
                ),
              },
            ];
            break;
          default:
            menuItems = [
              {
                label: 'Sonstige',
                expanded: false,
                items: _groupBy[type].map((it) =>
                  this._getMenuItem(it, featureStatus, featurePermissions),
                ),
              },
            ];
            break;
        }

        return menuItems.map(hideGroupWithoutVisibleItems);
      })
      .flat();

    this.menu$.next(_menu);
  }

  private _getMenuItem(
    feature: BdFeature<string>,
    featureStatus: {
      [featureId: string]: boolean;
    },
    featurePermissions: `${string}:${string}`[],
  ): MenuItem {
    const item: MenuItem = {
      id: feature.id,
      label: feature.displayName,
      disabled: false,
    };
    if (feature?.navigation?.externalLink) {
      item.url = feature.navigation.externalLink;
    } else {
      item.routerLink = feature?.navigation?.path;
    }
    if (feature?.navigation?.routerLinkActiveOptions) {
      item.routerLinkActiveOptions = feature.navigation.routerLinkActiveOptions;
    }

    if (
      feature?.navigation &&
      !this._isVisible(feature.navigation, featurePermissions)
    ) {
      item.visible = false;
      return item;
    }

    if (featureStatus[feature.id] === false) {
      item.icon = 'pi pi-exclamation-triangle'; // 'maintenance'
      item.disabled = true;
    } else if (this._needsPermission(feature)) {
      if (featurePermissions.indexOf(`${feature.id}:BASE`) === -1) {
        const disabledLink = feature.navigation?.disabledLink;
        if (disabledLink) {
          item.url = disabledLink;
          item.target = '_blank';
          item.icon = 'pi pi-external-link';
          item.styleClass = 'app-menu-cross-sell';
          delete item.routerLink;
          return item;
        }
        item.icon = 'pi pi-lock'; // 'missingPermission';
        item.disabled = true;
      }
    }

    if (
      feature?.navigation?.children &&
      feature.navigation.children.length > 0 &&
      !item.disabled
    ) {
      item.items = feature.navigation.children
        .filter((child) => this._isVisible(child, featurePermissions))
        .map((child) => ({
          label: child.title,
          routerLink: child.path,
          disabled: item.disabled,
          routerLinkActiveOptions: child.routerLinkActiveOptions,
        }));
    }
    return item;
  }

  /**
   * Checks if a navigation child is visible based on its visibilityPermissions.
   * If no visibilityPermissions are defined, the child is always visible.
   * If visibilityPermissions are defined, at least one must be present in featurePermissions.
   */
  private _isVisible(
    navigation: BdFeatureNavigation,
    featurePermissions: `${string}:${string}`[],
  ): boolean {
    if (
      !navigation.visibilityPermissions ||
      navigation.visibilityPermissions.length === 0
    ) {
      return true;
    }

    return navigation.visibilityPermissions.some((permission) =>
      featurePermissions.includes(permission),
    );
  }

  /**
   * Checks if the feature needs permission.
   * A feature needs no permission if no permissions are defined or if there is no `BASE` permission.
   * @param feature
   * @private
   */
  private _needsPermission(feature: BdFeature<string>): boolean {
    let needsPermission = true;

    if (
      feature.permissions === null ||
      (feature.permissions.length > 0 &&
        feature.permissions?.indexOf(`${feature.id}:BASE`) === -1)
    ) {
      needsPermission = false;
    }

    return needsPermission;
  }
}

/**
 * A group whose entries are all hidden by `visibilityPermissions` would render
 * as an empty header (e.g. "Jobs" for an admin without `ADMIN:JOBS`).
 */
function hideGroupWithoutVisibleItems(group: MenuItem): MenuItem {
  const items = group.items ?? [];
  return items.length > 0 && items.every((item) => item.visible === false)
    ? { ...group, visible: false }
    : group;
}
